import { useSyncExternalStore } from 'react'

/**
 * The interface skin: `fluent3d` (the redesign) or `classic` (the shipped
 * look). The server decides per organization (`GET /api/ui-config/`: the
 * FLUENT_UI_ENABLED setting or the organization's `fluent_ui` feature), so
 * there is no switch in the interface.
 *
 * Staff preview: `?skin=fluent3d` or `?skin=classic` overrides the server for
 * the rest of this browser tab (sessionStorage), and `?skin=server` clears it.
 */
export const SKINS = ['fluent3d', 'classic']
const PREVIEW_KEY = 'graphit-skin-preview'
const DEFAULT_SKIN = 'classic'
const listeners = new Set()

function readPreview() {
  if (typeof window === 'undefined') return null
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('skin')
    if (fromUrl === 'server') sessionStorage.removeItem(PREVIEW_KEY)
    else if (SKINS.includes(fromUrl)) sessionStorage.setItem(PREVIEW_KEY, fromUrl)
    const stored = sessionStorage.getItem(PREVIEW_KEY)
    return SKINS.includes(stored) ? stored : null
  } catch {
    return null
  }
}

const preview = readPreview()
let serverSkin = DEFAULT_SKIN
let current = preview || DEFAULT_SKIN

function apply(skin) {
  if (typeof document !== 'undefined') document.documentElement.dataset.skin = skin
}

apply(current)

function update(next) {
  if (!SKINS.includes(next) || next === current) return
  current = next
  apply(next)
  listeners.forEach((listener) => listener())
}

export function getSkin() {
  return current
}

/** For tests and previews: set the skin directly. */
export function setSkin(skin) {
  update(skin)
}

/**
 * Ask the server which skin this organization gets. A staff preview wins.
 * Never throws: on any failure the current skin stays.
 */
export async function loadServerSkin() {
  if (typeof window === 'undefined' || import.meta.env.MODE === 'test') return current
  try {
    const base = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '')
    const response = await fetch(`${base}/ui-config/`, { credentials: 'include', headers: { Accept: 'application/json' } })
    if (response.ok) {
      const { theme } = await response.json()
      if (SKINS.includes(theme)) serverSkin = theme
    }
  } catch {
    // Offline or blocked: keep what we have.
  }
  update(preview || serverSkin)
  return current
}

function subscribe(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** @returns {'fluent3d'|'classic'} */
export function useSkin() {
  return useSyncExternalStore(subscribe, getSkin, getSkin)
}
